import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import ActionButton from '../builder/ActionButton';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import ColorPicker from '../builder/ColorPicker.js';
import DynamicFields from '../builder/DynamicFields.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Model from '../builder/Model';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';
import Spinner from '../builder/Spinner';

import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout';
import CatchItClient from '../front/CatchItClient.js';

import Checkbox from '../builder/Checkbox.js';
import DisabledArea from '../builder/DisabledArea.js';
import SoundSelect from '../builder/SoundSelect.js';
import ActionLink from '../builder/ActionLink.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const CatchField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ uploading, setUploading ] = useState(false);

	const auto_img_hooks = (hook) => {
		switch(hook.type) {
			case 'AUTO_IMAGE_START':
				setUploading(true);
				break;

			case 'AUTO_IMAGE_END':
				setUploading(false);
				break;
		}
	}

	return (
		<DisabledArea disabled={uploading}>
			<Model model={model} change={change} errors={props.errors != undefined && props?.errors[model.id]}>
				<AutoPostImageUploader
					auto_url={`/promotions/auto_img/${props.promotion_id}`}
					auto_type='stages'
					changeMulti={props.changeMulti}
					hooks={auto_img_hooks}
					image={model.full_image}
					name="image"
					small_input={true}
					_size={{width:300, height:300}}
					_class={{ size:50 }}
				>Image</AutoPostImageUploader>

				<Input
					name="name"
					_class={{size:70}}
					autotranslate={false}
				>{i18n("***Name").replace("***","")}</Input>
			</Model>
		</DisabledArea>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	const min_time = 30;
	const max_time = 90;

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	const section_title = `${i18n("Items", 'ucfirst')} (${(stage?.catch_images ?? []).length})`;

	const dynamic_fields = [{
		type:'ok',
		label:"OK",
		title:(f) => (f?.name ?? "OK"),
		max:7,
	}, {
		type:'ko',
		label:"KO",
		title:(f) => (f?.name ?? "KO"),
		max:3,
	}, {
		type:'superok',
		label:"Super OK",
		title:(f) => (f?.name ?? "Super OK"),
		max:1,
	}, {
		type:'superko',
		label:"Super KO",
		title:(f) => (f?.name ?? "Super KO"),
		max:1,
	}];

	const speed_options = [
		{ k:'slow', v:i18n("Weak") },
		{ k:'normal', v:i18n("***Normal") },
		{ k:'fast', v:i18n("Strong") },
		{ k:'inferno', v:i18n("Black Hole") },
	];

	const num_items_options = [
		{ k:'less', v:i18n("Less") },
		{ k:'normal', v:i18n("***Normal") },
		{ k:'more', v:i18n("More") },
		{ k:'inferno', v:i18n("Inferno") },
	];

	let catch_images_for_play = stage?.catch_images ?? [];
	catch_images_for_play.map((item, i) => {
		catch_images_for_play[i] = { ...item, subtitle:item.type.toUpperCase() };
	});

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<Select
							name="catch_speed"
							values={speed_options}
							_class={{ size:40, first:true }}
							value={stage?.catch_speed ?? 'normal'}
						>Gravity</Select>

						<Select
							name="catch_num_items"
							values={num_items_options}
							_class={{ size:40 }}
							value={stage?.catch_num_items ?? 'normal'}
						>Number of items</Select>

						<Range
							min={min_time}
							max={max_time}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							_class={{ size:50 }}
							name="game_time"
							value={(stage.game_time != undefined) ? Math.max(stage.game_time, min_time) : 60}
						>Time of game</Range>

						<Checkbox
							name='catch_rotate_on_fall'
							value={stage?.catch_rotate_on_fall ?? true}
						>Rotate while falling</Checkbox>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_catch_player_image}
							name="catch_player_image"
							small_input={true}
							after_component={<ActionLink
								_href={`${window.zendesk_base_url}28939111073684/#template`}
								_target="_blank"
							>How to design the image</ActionLink>}
							_size={{ width:200, height:120 }}
						>Image to catch the elements</AutoPostImageUploader>
					</Model>
				</article>

				<article class="gui_not_in_top_huge gui_not_in_bottom">
					<DynamicFields
						section_title={section_title}
						onChange={(v) => changeSidebar('catch_images', v)}
						field_types={dynamic_fields}
						add_button_props={{
							icon:'knk-add-item mainButton',
						}}
						add_field_label="Add new element"
						sortable={false}
						fields={catch_images_for_play}
					>
						<CatchField
							promotion_id={stage.promotion_id}
							errors={errors}
							intl={props.intl}
						/>
					</DynamicFields>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<CatchItClient
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_playable={false}
									section="settings"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	const section_title = `${i18n("Items", 'ucfirst')} (${(stage?.catch_images ?? []).length})`;

	const dynamic_fields = [{
		type:'ok',
		label:"OK",
		title:(f) => (f?.name ?? "OK"),
		max:3,
	}, {
		type:'ko',
		label:"KO",
		title:(f) => (f?.name ?? "KO"),
		max:3,
	}, {
		type:'superok',
		label:"Super OK",
		title:(f) => (f?.name ?? "Super OK"),
		max:1,
	}, {
		type:'superko',
		label:"Super KO",
		title:(f) => (f?.name ?? "Super KO"),
		max:1,
	}];

	const sounds_ok = [
		{ k:'none', v:i18n("***None") },
		{ k:'success', v:i18n("Success") },
		{ k:'slide', v:i18n("Slide") },
		{ k:'cross', v:i18n("Cross") },
		{ k:'flag', v:i18n("Flag") },
		{ k:'hint', v:i18n("Hint") },
		{ k:'unplug', v:i18n("Unplug") },
	];

	const sounds_ko = [
		{ k:'none', v:i18n("***None") },
		{ k:'error_backflip', v:i18n("Backflip") },
		{ k:'error_buzzer', v:i18n("Buzzer") },
		{ k:'funny_laugh', v:i18n("Funny laugh") },
		{ k:'unplug', v:i18n("Unplug") },
	];

	let catch_images_for_play = stage?.catch_images ?? [];
	catch_images_for_play.map((item, i) => {
		catch_images_for_play[i] = { ...item, subtitle:item.type.toUpperCase() };
	});

	const used_types = ['ok'];
	if(stage.catch_images.filter((i) => (i.type == 'ko')).length > 0) used_types.push('ko');
	if(stage.catch_images.filter((i) => (i.type == 'superok')).length > 0) used_types.push('superok');
	if(stage.catch_images.filter((i) => (i.type == 'superko')).length > 0) used_types.push('superko');

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_catch_bg_image}
							name="catch_bg_image"
							small_input={true}
							_size={{ width:800, height:800 }}
							_class={{ first:true }}
						>Background image</AutoPostImageUploader>

						{(!isNotEmpty(stage, 'catch_bg_image')) && (
							<ColorPicker
								_class={{ size:60 }}
								_hide_code={true}
								name="catch_bg_color"
								color={stage?.catch_bg_color ?? 'rgba(230, 230, 230, 1)'}
							>Background color</ColorPicker>
						)}

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>

						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>

				{((stage?.silence ?? false) === false) && used_types.map((type) => {
					return (
						<article class="gui_not_in_top_huge">
							<BuilderSubSectionTitle
								title={props.intl.formatMessage({ id:"Effect when picking up ({type})" }, { type:i18n(type, 'uppercase') })}
								autotranslate={false}
								icon='knk-developers'
							/>

							<Model key={`info_${type}`} model={stage} change={changeSidebar}>
								<SoundSelect
									name={`catch_effects.${type}.sound_fx`}
									values={(type.indexOf('ok') != -1) ? sounds_ok : sounds_ko}
									_class={{ size:60 }}
								>Sound FX</SoundSelect>
							</Model>
						</article>
					)
				})}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<CatchItClient
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_playable={false}
									section="settings"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar without_ctas={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						<CatchItClient
							stage={{ Stage:stage }}
							promotion={props.promotion}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const CatchItBuilder = ({ base_route, stage, ...props }) => {
	const basename = `/stages/${stage.id}/falling`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<CatchItClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['out_of_time', 'out_of_moves']}
							has_threshold={true}
						>
							<CatchItClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
};

export default injectIntl(CatchItBuilder);